import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap, withLatestFrom, exhaustMap, concatMap, exhaust, filter } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogRef } from '@angular/material/dialog';
import * as invoicesSelectors from '../store/selectors/invoices.selectors';
import * as invoicesActions from '../store/actions/invoices.actions';
import { InvoicesService } from '../services/invoices.service';
import { of } from 'rxjs';

@Injectable()
export class InvoicesEffects {

 loadInvoices$ = createEffect(() =>
  this.actions$.pipe(
    ofType(invoicesActions.loadInvoices),
    mergeMap(({ filters }) =>
      this.invoicesService.getInvoices(filters).pipe(
        map((pagedInvoices) => invoicesActions.loadInvoicesSuccess({ invoices: pagedInvoices })),
        catchError((error) => {
          const msg = error?.error?.error || 'No se pudieron cargar las facturas';
          this.toastr.error(msg, 'Error');
          return of(invoicesActions.loadInvoicesFailure({ error }));
        })
      )
    )
  )
);

 createInvoices$ = createEffect(() =>
  this.actions$.pipe(
    ofType(invoicesActions.createInvoices),
    withLatestFrom(this.store.pipe(select(invoicesSelectors.selectInvoicesFilter))),
    mergeMap(([action, filters]) =>
      this.invoicesService.createInvoices(filters).pipe(
        tap((invoices) => {
          this.toastr.success(
            `Se crearon ${invoices.total_items} factura(s) exitosamente.`,
            'Facturación completada'
          );
        }),
        map((invoices) => invoicesActions.createInvoicesSuccess({ invoices })),
        catchError((error) => {
          const msg = error?.error?.error || 'No se pudieron crear las facturas';
          this.toastr.error(msg, 'Error');
          return of(invoicesActions.createInvoicesFailure({ error }));
        })
      )
    )
  )
);

  loadInvoiceById$ = createEffect(() =>
  this.actions$.pipe(
    ofType(invoicesActions.loadInvoiceById),
    withLatestFrom(this.store.pipe(select(invoicesSelectors.selecteSelectedInvoiceId))),
    exhaustMap(([action, selectedInvoiceId]) =>
      this.invoicesService.getInvoiceById(selectedInvoiceId).pipe(
        map((invoice) => invoicesActions.loadInvoiceByIdSuccess({ invoice })),
        catchError((error) => {
          const msg = error?.error?.error || 'No se pudo cargar la factura';
          this.toastr.error(msg, 'Error');
          return of(invoicesActions.loadInvoiceByIdFailure({ error }));
        })
      )
    )
  ));


  navigateToInvoiceDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(invoicesActions.loadInvoiceByIdSuccess),
        tap(({ invoice }) => {
          if (invoice && invoice.id) {
            this.router.navigate([
              '/payments-support/details',
              invoice.id
            ]);
          }
        })
      ),
    { dispatch: false }
  );

  updateServicePrice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invoicesActions.updateServicePrice),
      mergeMap(({ invoiceId, serviceId, newPrice }) =>
        this.invoicesService.updateServicePrice(invoiceId, serviceId, newPrice).pipe(
          map((invoice) => invoicesActions.updateServicePriceSuccess({ invoice })),
          catchError((error) => {
            const msg = error?.error?.error || error?.error?.message || 'No se pudo actualizar el precio del servicio';
            this.toastr.error(msg, 'Error');
            return of(invoicesActions.updateServicePriceFailure({ error }));
          })
        )
      )
    )
  );

  updateInvoiceReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invoicesActions.updateInvoiceReference),
      mergeMap(({ invoiceId, payment_reference_number }) =>
        this.invoicesService.updateInvoiceReference(invoiceId, payment_reference_number).pipe(
          map((invoice) => invoicesActions.updateInvoiceReferenceSuccess({ invoice })),
          tap(() => {
            this.toastr.success('Referencia de pago actualizada correctamente', 'Factura');
          }),
          catchError((error) => {
            const msg = error?.error?.error || 'No se pudo actualizar la referencia de pago';
            this.toastr.error(msg, 'Error');
            return of(invoicesActions.updateInvoiceReferenceFailure({ error }));
          })
        )
      )
    )
  );

  regenerateInvoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invoicesActions.regenerateInvoice),
      mergeMap(({ invoiceId }) =>
        this.invoicesService.regenerateInvoice(invoiceId).pipe(
          map((invoice) => invoicesActions.regenerateInvoiceSuccess({ invoice })),
          tap(() => {
            this.toastr.success('Factura regenerada correctamente', 'Factura');
          }
          ),
          catchError((error) => {
            const msg = error?.error?.error || 'No se pudo regenerar la factura';
            this.toastr.error(msg, 'Error');
            return of(invoicesActions.regenerateInvoiceFailure({ error }));
          })
        )
      )
    )
  );

  recalculateInvoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invoicesActions.recalculateInvoices),
      withLatestFrom(this.store.pipe(select(invoicesSelectors.selectInvoicesFilter))),
      mergeMap(([action, filters]) =>
        this.invoicesService.recalculateInvoicesByRange(filters?.from_date!, filters?.to_date!).pipe(
          tap(result => {
            this.toastr.success(result.message || 'Facturas recalculadas', 'Recalcular facturas');
          }),
          mergeMap(result => [
            invoicesActions.recalculateInvoicesSuccess({ result }),
            invoicesActions.loadInvoices({ filters })
          ]),
          catchError(error => {
            this.toastr.error('Error al recalcular facturas', 'Error');
            return of(invoicesActions.recalculateInvoicesFailure({ error }));
          })
        )
      )
    )
  );

  recalculateInvoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(invoicesActions.recalculateInvoice),
      mergeMap(({ invoice_id }) =>
        this.invoicesService.recalculateInvoiceById(invoice_id).pipe(
          tap(result => {
            this.toastr.success(result.message || 'Factura recalculada', 'Recalcular factura');
          }),
          mergeMap(result => [
            invoicesActions.recalculateInvoiceSuccess({ result }),
            invoicesActions.loadInvoiceById({ id: invoice_id })
          ]),
          catchError(error => {
            this.toastr.error('Error al recalcular factura', 'Error');
            return of(invoicesActions.recalculateInvoiceFailure({ error }));
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private toastr: ToastrService,
    private router: Router,
    private dialog: MatDialog,
    private invoicesService: InvoicesService
  ) {}



}